import React, { useEffect, useMemo, useState } from "react";
import type { ZipAnalysis, ZipTreeNode } from "../utils/zipAnalyzer";
import styles from "./AddVersionDialog.module.css";

interface ZipTreeDropdownProps {
  analysis: ZipAnalysis | null;
  isOpen: boolean;
  selectedPath: string;
  onSelect: (path: string) => void;
}

export function ZipTreeDropdown({
  analysis,
  isOpen,
  selectedPath,
  onSelect
}: ZipTreeDropdownProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!analysis) {
      setExpandedNodes(new Set());
      return;
    }
    setExpandedNodes(createDefaultExpansion(analysis));
  }, [analysis]);

  const toggleNode = (path: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  };

  const tree = useMemo(() => {
    if (!analysis?.nodes.length) {
      return (
        <div className={styles.zipDropdownEmpty}>
          This archive does not contain any files we can display.
        </div>
      );
    }

    const renderNodes = (nodes: ZipTreeNode[]): React.ReactNode =>
      nodes.map((node) => {
        const isExpanded = expandedNodes.has(node.path);
        const isSelected =
          !node.isDirectory && selectedPath === node.path;
        return (
          <div
            key={node.id}
            className={styles.zipTreeNode}
            role="treeitem"
            aria-expanded={node.isDirectory ? isExpanded : undefined}
          >
            <div className={styles.zipTreeRow}>
              {node.isDirectory ? (
                <button
                  type="button"
                  className={styles.zipTreeToggle}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => toggleNode(node.path)}
                  aria-label={
                    isExpanded
                      ? `Collapse ${node.name}`
                      : `Expand ${node.name}`
                  }
                >
                  {isExpanded ? "▾" : "▸"}
                </button>
              ) : (
                <span className={styles.zipTreeSpacer} />
              )}
              <button
                type="button"
                className={`${styles.zipTreeLabel} ${
                  node.isDirectory ? styles.zipTreeDir : styles.zipTreeFile
                } ${isSelected ? styles.zipTreeSelected : ""}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() =>
                  node.isDirectory ? toggleNode(node.path) : onSelect(node.path)
                }
                title={node.path}
              >
                <span
                  className={styles.zipTreeIcon}
                  data-type={node.isDirectory ? "dir" : "file"}
                />
                <span className={styles.zipTreeText}>
                  {node.name || (node.isDirectory ? "/" : node.path)}
                </span>
              </button>
            </div>
            {node.isDirectory && isExpanded && node.children.length > 0 && (
              <div className={styles.zipTreeChildren} role="group">
                {renderNodes(node.children)}
              </div>
            )}
          </div>
        );
      });

    return <div role="tree">{renderNodes(analysis.nodes)}</div>;
  }, [analysis, expandedNodes, onSelect, selectedPath]);

  if (!isOpen || !analysis) return null;

  return (
    <div className={styles.zipDropdown}>
      <div className={styles.zipDropdownHeader}>
        <span>Archive explorer</span>
        <span className={styles.zipHelperText}>
          Select a file to fill the executable path.
        </span>
      </div>
      <div className={styles.zipDropdownList}>{tree}</div>
    </div>
  );
}

function createDefaultExpansion(result: ZipAnalysis) {
  const expanded = new Set<string>();
  if (result.heuristics.suggestedExecutable) {
    const segments = result.heuristics.suggestedExecutable
      .split("/")
      .filter(Boolean);
    segments.pop();
    let current = "";
    for (const segment of segments) {
      current = current ? `${current}/${segment}` : segment;
      expanded.add(current);
    }
  } else if (result.nodes.length === 1 && result.nodes[0].isDirectory) {
    expanded.add(result.nodes[0].path);
  }
  return expanded;
}
